import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, DownloadIcon, MapPinIcon, MailIcon, PhoneIcon, UserIcon, CheckCircle2Icon, StarIcon } from 'lucide-react';
type Step = 'search' | 'export';
const searchResults = [{
  name: 'Ocean View Dining',
  category: 'Restaurant',
  location: 'Miami Beach, FL',
  owner: 'Michael R.',
  email: 'hello@oceanview.com',
  phone: '(305) 555-0199',
  rating: 4.8,
  match: 97
}, {
  name: 'Coral Gables Bistro',
  category: 'Restaurant',
  location: 'Coral Gables, FL',
  owner: 'Ana L.',
  email: 'info@coralgablesbistro.com',
  phone: '(305) 555-0234',
  rating: 4.6,
  match: 94
}, {
  name: 'Brickell Sushi House',
  category: 'Restaurant',
  location: 'Brickell, FL',
  owner: 'Ken T.',
  email: 'ken@brickellsushi.com',
  phone: '(305) 555-0312',
  rating: 4.9,
  match: 91
}];
export function MarketingDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [exportPhase, setExportPhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exportTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Auto-start search on mount
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchPhase('done');
    }, 1200);
  };
  const startExport = () => {
    setExportPhase('loading');
    if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    exportTimerRef.current = setTimeout(() => {
      setExportPhase('done');
    }, 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') {
      startSearch();
    }
    if (step === 'export') {
      startExport();
    }
  };
  return <section className="py-24 bg-white" data-id="element-4836">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4837">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4838">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4839">
            See how Openmart powers{' '}
            <span className="text-blue-600" data-id="element-4840">local business marketing</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4841">
            Find the right local businesses, enrich their contacts, and export
            audiences ready for your next campaign.
          </p>
        </div>

        {/* Step tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-4842">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-4843">
            <SearchIcon className="w-4 h-4" data-id="element-4844" />
            Find local businesses
          </button>
          <button onClick={() => handleTabSwitch('export')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'export' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-4845">
            <DownloadIcon className="w-4 h-4" data-id="element-4846" />
            Export audience
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-4847">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-4848">
              {/* Left: Search form */}
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-4849">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4850">
                  Search local businesses
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-4851">
                  Define your target audience by category and location.
                </p>

                <div className="space-y-5" data-id="element-4852">
                  <div data-id="element-4853">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-4854">
                      Business type
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-4855">
                      Restaurants
                    </div>
                  </div>
                  <div data-id="element-4856">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-4857">
                      Location
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-4858">
                      Miami, FL
                    </div>
                  </div>
                </div>

                {/* Status indicator instead of button */}
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-4859">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-4860">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-4861" />
                      Searching...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-4862">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-4863" />
                      1,245 results found
                    </span>}
                </div>
              </div>

              {/* Right: Results */}
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-4864">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-4865">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-4866" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-4867">
                      Finding restaurants in Miami...
                    </p>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-4868">
                      Enriching contacts automatically
                    </p>
                  </div>}

                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-4869">
                    <div className="flex items-center justify-between mb-6" data-id="element-4870">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-4871">
                        Results
                      </h3>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-4872">
                        1,245 local businesses found
                      </span>
                    </div>

                    <div className="space-y-3" data-id="element-4873">
                      {searchResults.map((result, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-4874">
                          <div className="flex items-start justify-between mb-3" data-id="element-4875">
                            <div data-id="element-4876">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-4877">
                                {result.name}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-4878">
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-4879">
                                  <MapPinIcon className="w-3 h-3" data-id="element-4880" />
                                  {result.location}
                                </span>
                                <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-4881">
                                  <StarIcon className="w-3 h-3 fill-yellow-400 text-yellow-400" data-id="element-4882" />
                                  {result.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-slate-600" data-id="element-4883">
                            <span className="flex items-center gap-1.5" data-id="element-4884">
                              <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4885" />
                              {result.owner}
                            </span>
                            <span className="flex items-center gap-1.5" data-id="element-4886">
                              <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-4887" />
                              {result.email}
                            </span>
                            <span className="flex items-center gap-1.5" data-id="element-4888">
                              <PhoneIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-4889" />
                              {result.phone}
                            </span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'export' && <motion.div key="export" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-4890">
              {/* Left: Export config */}
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-4891">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4892">
                  Export audience
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-4893">
                  Configure your export for ad platforms like Meta Ads.
                </p>

                <div className="space-y-5" data-id="element-4894">
                  <div data-id="element-4895">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-4896">
                      Audience name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-4897">
                      Miami restaurants Q1
                    </div>
                  </div>
                  <div data-id="element-4898">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-4899">
                      Export format
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 flex items-center justify-between" data-id="element-4900">
                      <span data-id="element-4901">Meta Ads Custom Audience</span>
                      <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" data-id="element-4902">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" data-id="element-4903" />
                      </svg>
                    </div>
                  </div>
                  <div data-id="element-4904">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-4905">
                      Include fields
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-4906">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-4907">
                        Email
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-4908">
                        Phone
                      </span>
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg border border-blue-100" data-id="element-4909">
                        Name
                      </span>
                    </div>
                  </div>
                </div>

                {/* Status indicator instead of button */}
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-4910">
                  {exportPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-4911">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-4912" />
                      Generating export...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-4913">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-4914" />
                      Export ready
                    </span>}
                </div>
              </div>

              {/* Right: Export result */}
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-4915">
                {exportPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-4916">
                    <div className="w-full max-w-sm" data-id="element-4917">
                      <p className="text-base font-semibold text-slate-700 mb-4 text-center" data-id="element-4918">
                        Building your audience...
                      </p>
                      <div className="space-y-4" data-id="element-4919">
                        <div data-id="element-4920">
                          <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-4921">
                            <span className="text-slate-500" data-id="element-4922">
                              Matching emails
                            </span>
                            <motion.span className="text-blue-600" initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: 0.3
                      }} data-id="element-4923">
                              1,102 / 1,245
                            </motion.span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden" data-id="element-4924">
                            <motion.div className="h-full bg-blue-500 rounded-full" initial={{
                        width: '0%'
                      }} animate={{
                        width: '88%'
                      }} transition={{
                        duration: 1.2,
                        ease: 'easeOut'
                      }} data-id="element-4925" />
                          </div>
                        </div>
                        <div data-id="element-4926">
                          <div className="flex justify-between text-xs font-medium mb-1.5" data-id="element-4927">
                            <span className="text-slate-500" data-id="element-4928">
                              Matching phones
                            </span>
                            <motion.span className="text-blue-600" initial={{
                        opacity: 0
                      }} animate={{
                        opacity: 1
                      }} transition={{
                        delay: 0.5
                      }} data-id="element-4929">
                              1,198 / 1,245
                            </motion.span>
                          </div>
                          <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden" data-id="element-4930">
                            <motion.div className="h-full bg-blue-500 rounded-full" initial={{
                        width: '0%'
                      }} animate={{
                        width: '96%'
                      }} transition={{
                        duration: 1.2,
                        delay: 0.2,
                        ease: 'easeOut'
                      }} data-id="element-4931" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {exportPhase === 'done' && <motion.div initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center justify-center min-h-[350px]" data-id="element-4932">
                    <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-4933">
                      <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6" data-id="element-4934">
                        <CheckCircle2Icon className="w-9 h-9 text-green-500" data-id="element-4935" />
                      </div>

                      <h4 className="text-xl font-bold text-slate-900 mb-2" data-id="element-4936">
                        Audience ready for ad platforms
                      </h4>
                      <p className="text-sm text-slate-500 mb-8" data-id="element-4937">
                        Your custom audience file is ready to upload.
                      </p>

                      <div className="bg-slate-50 rounded-2xl p-6 text-left space-y-4 mb-8" data-id="element-4938">
                        <div className="flex justify-between items-center" data-id="element-4939">
                          <span className="text-sm text-slate-500" data-id="element-4940">
                            Audience name
                          </span>
                          <span className="text-sm font-semibold text-slate-900" data-id="element-4941">
                            Miami restaurants Q1
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-4942">
                          <span className="text-sm text-slate-500" data-id="element-4943">
                            Total contacts
                          </span>
                          <span className="text-sm font-semibold text-slate-900" data-id="element-4944">
                            1,245
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-4945">
                          <span className="text-sm text-slate-500" data-id="element-4946">
                            Matched emails
                          </span>
                          <span className="text-sm font-semibold text-green-600" data-id="element-4947">
                            1,102 (88%)
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-4948">
                          <span className="text-sm text-slate-500" data-id="element-4949">
                            Matched phones
                          </span>
                          <span className="text-sm font-semibold text-green-600" data-id="element-4950">
                            1,198 (96%)
                          </span>
                        </div>
                        <div className="flex justify-between items-center" data-id="element-4951">
                          <span className="text-sm text-slate-500" data-id="element-4952">Format</span>
                          <span className="text-sm font-semibold text-slate-900" data-id="element-4953">
                            Meta Ads Custom Audience
                          </span>
                        </div>
                      </div>

                      <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="block w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center" data-id="element-4954">
                        Download audience CSV in Openmart
                      </a>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}